import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import PropertyService from "../../api/PropertyService";
import { CollapseArrowIcon } from "../../icons/CollapseArrow/CollapseArrow";
import { EditIcon } from "../../icons/EditIcon/EditIcon";
import { EyeIcon } from "../../icons/EyeIcon/EyeIcon";
import { MapIcon } from "../../icons/MapIcon/MapIcon";
import { RootState } from "../../store";
import { ROLES } from "../../store/user-slice";
import { Document } from "../../types/document";
import { Crop, MonitorPinAbbr } from "../../types/map";
import { MonitorService } from "../../types/monitor";
import FullScreenPreview from "../FullScreenPreview/FullScreenPreview";
import PinCropMap from "../PinCropMap/PinCropMap";
import { ServiceCount } from "../ServiceCount/ServiceCount";

import styles from "./card-wrapper.module.css";

interface Props {
  title?: any;
  children: any;
  collapsed?: boolean;
  color: "white" | "gray" | "lightGray";
  service?:
    | "infestation"
    | "noInfestation"
    | "maintenance"
    | "unreachable"
    | "adjustment";
  addInfoIcon?: boolean;
  infestationCount?: number;
  onClick?: () => void;
  showMapIcon?: boolean;
  monitors?: MonitorService[];
  map?: Document;
  isCustomerLookup?: boolean;
  onEditClick?: () => void;
  propertyAreaId?: string;
  isPropertyAreaHidden?: boolean;
  onToggleHidden?: (propertyAreaId: string) => void;
}

const CARD_WRAPPER_TITLE_BACKGROUND: Record<
  "white" | "gray" | "lightGray",
  string
> = {
  white: styles["card__wrapper__content--white"],
  gray: styles["card__wrapper__content--gray"],
  lightGray: styles["card__wrapper__content--light-gray"],
};

export const CardWrapper = ({
  title,
  children,
  collapsed = false,
  color,
  service,
  infestationCount,
  onClick,
  addInfoIcon,
  showMapIcon,
  monitors,
  map,
  isCustomerLookup = false,
  onEditClick,
  propertyAreaId,
  isPropertyAreaHidden = false,
  onToggleHidden,
}: Props) => {
  const navigate = useNavigate();
  const [reversed, setReversed] = useState(false);
  const [reversedBottom, setReversedBottom] = useState(false);
  const onClickShowMap = () => {
    setReversed(!reversed);
    setShowMap(!showMap);
  };
  const onClickShowMapBottom = () => {
    setReversedBottom(!reversedBottom);
    setShowMapBottom(!showMapBottom);
  };
  const [crops, setCrops] = useState<Crop[]>([]);
  const [showMap, setShowMap] = useState(false);
  const [showMapBottom, setShowMapBottom] = useState(false);
  const [monitorPinAbbrs, setMonitorPinAbbrs] = useState<MonitorPinAbbr[]>([]);

  const appointmentPropAreas = useSelector(
    (state: RootState) =>
      state.appointmentPropertyAreas.appointmentPropertyAreas
  );

  const user = useSelector((state: RootState) => state.user);

  const isAdmin = user?.currentUser?.roles?.includes(ROLES.ADMIN);

  const displayedTitle =
    !isCustomerLookup && propertyAreaId
      ? appointmentPropAreas.find((area) => area.id === propertyAreaId)
          ?.label || title
      : title;

  const displayedMap =
    !isCustomerLookup && propertyAreaId
      ? (appointmentPropAreas.find((area) => area.id === propertyAreaId)?.map ??
        map)
      : map;

  useEffect(() => {
    if (monitors) {
      const mappedCrops = monitors?.map((monitor: any) => ({
        x: Number(monitor.x),
        y: Number(monitor.y),
      }));

      const mappedMonitorPinAbbrs = monitors?.map((monitor: any) => ({
        name: monitor.monitorType.slice(0, 3).toUpperCase(),
        number: (monitor.isTemporary ? "T" : "") + monitor.position.toString(),
      }));
      setCrops(mappedCrops);
      setMonitorPinAbbrs(mappedMonitorPinAbbrs);
    }
  }, [monitors]);

  return (
    <div className={styles["card__wrapper"]}>
      <div
        className={`${
          displayedTitle
            ? `${styles["card__wrapper__content"]} ${CARD_WRAPPER_TITLE_BACKGROUND[color]}`
            : ""
        } ${
          !collapsed && displayedTitle
            ? styles["monitors__wrapper__content--shadow"]
            : ""
        }`}
      >
        <div
          className={`${styles["card__wrapper__content--title"]} ${
            color === "gray" && styles["card__wrapper__content--title--gray"]
          }`}
        >
          <div
            className={`${styles["card__wrapper__content--title-text"]} ${
              showMapIcon && styles["showMap"]
            }`}
            onClick={onClick}
          >
            <span className={styles["card__wrapper__content--title--area"]}>
              {displayedTitle}
            </span>
            
            {isCustomerLookup && (
              <div className={styles["card__wrapper__content--title-icons"]}>
                {isAdmin && (
                  <div
                    onClick={async (event) => {
                      event.stopPropagation();
                      if (!isAdmin) return;

                      try {
                        await PropertyService.ToggleHidden(propertyAreaId!);

                        if (onToggleHidden) {
                          onToggleHidden(propertyAreaId!);
                        }
                      } catch (error) {
                        console.error("Failed to toggle hidden state:", error);
                      }
                    }}
                  >
                    <EyeIcon enabled={isPropertyAreaHidden} />
                  </div>
                )}

                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    if (onEditClick) {
                      onEditClick();
                    } else if (propertyAreaId) {
                      navigate(`/property-areas/edit`, {
                        state: { propertyAreaId },
                      });
                    }
                  }}
                >
                  <EditIcon color="darkGray" />
                </div>
              </div>
            )}
            {showMapIcon && !isCustomerLookup && (
              <div
                className={styles["card__wrapper__content--title-icons"]}
                onClick={(event) => {
                  event.stopPropagation();
                  onClickShowMap();
                }}
              >
                <CollapseArrowIcon reversed={reversed} />
                <MapIcon color="white"></MapIcon>
              </div>
            )}
          </div>
          {displayedMap && crops && monitorPinAbbrs && showMap && (
            <div className={styles["image-container"]}>
              <div className={styles["image"]}>
                <PinCropMap
                  map={displayedMap}
                  crop={crops}
                  monitorPinAbbr={monitorPinAbbrs}
                  onCropChange={() => {}}
                  isDraggable={false}
                  zoom={false}
                  onPinClick={() => {}}
                />
                <FullScreenPreview>
                  <PinCropMap
                    map={displayedMap}
                    crop={crops}
                    monitorPinAbbr={monitorPinAbbrs}
                    onCropChange={() => {}}
                    isDraggable={true}
                    onPinClick={() => {}}
                  />
                </FullScreenPreview>
              </div>
            </div>
          )}

          {service && (
            <ServiceCount
              count={infestationCount ?? 0}
              service={service}
              addInfoIcon={addInfoIcon ?? true}
              onClick={onClick}
            />
          )}
        </div>
      </div>
      {
        <ul
          className={`${styles["card__wrapper__list"]} ${
            color === "gray" && styles["card__wrapper__list--gray"]
          }`}
        >
          {!isPropertyAreaHidden || !isCustomerLookup ? children : null}
        </ul>
      }
      {isCustomerLookup && (
        <div
          className={`${styles["card__wrapper__content"]} ${
            color === "gray" && styles["card__wrapper__content--title--gray"]
          }`}
          style={{
            padding: "0px 20px 13px 20px",
            backgroundColor: isPropertyAreaHidden ? "#F2F2F2" : "#FFFFFF",
          }}
        >
          <div
            className={`${styles["card__wrapper__content--title-text"]} ${
              showMapIcon && styles["showMap"]
            }`}
            onClick={onClick}
            style={{
              borderTop: "1px solid #c4c4c4",
              paddingTop: "13px",
              marginRight: 0,
            }}
          >
            Karte anzeigen
            {showMapIcon && (
              <div
                className={styles["card__wrapper__content--title-icons"]}
                onClick={(event) => {
                  event.stopPropagation();
                  onClickShowMapBottom();
                }}
              >
                <CollapseArrowIcon reversed={reversedBottom} />
                <MapIcon color="white"></MapIcon>
              </div>
            )}
          </div>
        </div>
      )}
      {map && crops && monitorPinAbbrs && showMapBottom && (
        <div className={styles["image-container"]}>
          <div className={styles["image"]}>
            <PinCropMap
              map={map}
              crop={crops}
              monitorPinAbbr={monitorPinAbbrs}
              onCropChange={() => {}}
              isDraggable={false}
              zoom={false}
              onPinClick={() => {}}
            />
            <FullScreenPreview>
              <PinCropMap
                map={map}
                crop={crops}
                monitorPinAbbr={monitorPinAbbrs}
                onCropChange={() => {}}
                isDraggable={true}
                onPinClick={() => {}}
              />
            </FullScreenPreview>
          </div>
        </div>
      )}
    </div>
  );
};
